import { Button, Select } from '@/components';
import { Show, createSignal, onCleanup } from 'solid-js';
import { useReleaseBaselines } from './hooks';

/**
 * Draft step ③ — pick what this build's change log compares against
 * (releases.base_release_id). One flat list of earlier builds; Auto lets the
 * server resolve the baseline.
 */
export const BaselineSection = (props: {
  releaseId: string;
  baseReleaseId: string | null;
  canEdit: boolean;
  saving: boolean;
  onSave: (baseReleaseId: string | null) => void;
}) => {
  const [search, setSearch] = createSignal('');
  const [query, setQuery] = createSignal('');
  let timer: ReturnType<typeof setTimeout> | undefined;
  const searchBaselines = (value: string) => {
    setSearch(value.trim());
    clearTimeout(timer);
    timer = setTimeout(() => setQuery(value.trim()), 300);
  };
  onCleanup(() => clearTimeout(timer));
  const releases = useReleaseBaselines(() => props.releaseId, query);
  const options = () => releases.data?.pages.flatMap((p) => p.data) ?? [];
  const automaticBaseline = () => releases.data?.pages[0]?.meta.automatic;
  const [picked, setPicked] = createSignal<{ id: string; label: string } | null>(null);
  const value = () => picked()?.id ?? props.baseReleaseId ?? '';
  const currentBaseline = () =>
    props.baseReleaseId ? releases.data?.pages[0]?.meta.selected : automaticBaseline();
  const optionLabel = (r: { version: string; buildNumber: number }) =>
    `${r.version} #${r.buildNumber}`;
  const autoLabel = () => {
    const automatic = automaticBaseline();
    return automatic ? `Auto baseline — ${optionLabel(automatic)}` : 'Auto baseline';
  };
  const selectedLabel = () => {
    const current = currentBaseline();
    return picked()?.label ?? (props.baseReleaseId && current ? optionLabel(current) : autoLabel());
  };

  return (
    <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
      <h2 class="mb-2 font-medium">Baseline</h2>
      <div class="flex flex-col gap-3">
        <p class="text-sm text-neutral-600 dark:text-neutral-300">
          {currentBaseline()
            ? `Comparing against ${currentBaseline()?.version} #${currentBaseline()?.buildNumber}.`
            : "Pick what this build's change log compares against."}
        </p>

        <Show when={props.canEdit}>
          <div class="min-w-44 self-start">
            <Select
              label="Baseline"
              value={value()}
              onChange={(id) => {
                const row = options().find((r) => r.id === id);
                setPicked({ id, label: row ? optionLabel(row) : autoLabel() });
              }}
              onSearch={searchBaselines}
              onLoadMore={
                releases.hasNextPage
                  ? () => {
                      void releases.fetchNextPage();
                    }
                  : undefined
              }
              loading={search() !== query() || releases.isFetching}
              error={
                releases.isError ? 'Failed to load baselines. Search again to retry.' : undefined
              }
              selectedLabel={selectedLabel()}
              options={[
                {
                  value: '',
                  label: autoLabel(),
                },
                ...options().map((r) => ({
                  value: r.id,
                  label: `${r.version} #${r.buildNumber}`,
                })),
              ]}
            />
          </div>

          <div class="flex gap-2">
            <Button
              onClick={() => props.onSave(value() || null)}
              loading={props.saving}
              disabled={value() === (props.baseReleaseId ?? '')}
            >
              Set baseline
            </Button>
            <Show when={props.baseReleaseId}>
              <Button variant="secondary" onClick={() => props.onSave(null)}>
                Clear
              </Button>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
};
